import { Button, cn } from '@spiaggeit/spit-ui'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BackButton } from '@/components/BackButton/index.tsx'
import { ButtonLink } from '@/components/ButtonLink'
import { useAppSelector } from '@/hooks/store.ts'
import { useRefundPolicyDialogProps } from '@/hooks/useRefundPolicyDialogProps.ts'
import { appSlice } from '@/store/appSlice.ts'
import { cartSlice } from '@/store/cartSlice/index.ts'
import { licenseSlice } from '@/store/licenseSlice'

import { PaymentMethod } from '@/store/selectors/selectPaymentMethods.ts'
import { formatPrice } from '@/utils/price.ts'
import { BookingPolicyDialog } from '../../BookingPolicyDialog'
import { RefundPolicyDialog } from '../../RefundPolicyDialog'
import { RefundPolicy } from '../../SelectedElementDrawer/RefundPolicy.tsx'
import { TranslatedPolicyText } from '../../TranslatedPolicyText'
export interface ReservationFooterProps {
  nextPath?: string
  isFinalPayment?: boolean
  isDisabled?: boolean
  total: number
  paymentMethod?: PaymentMethod
  className?: string
}

export const ReservationFooter = (props: ReservationFooterProps) => {
  const license = useAppSelector(licenseSlice.selectors.license)
  const beachPrivacyPolicy = license?.bookingPolicyUrl
  const regulation = license?.bookingPolicy
  const isKiosk = useAppSelector(appSlice.selectors.isKiosk)
  const isPayingWithCard = useAppSelector(cartSlice.selectors.isPayingWithCard)
  const isCreatingCart = useAppSelector(cartSlice.selectors.isCreateCartPending)
  const isPayingWithStripe = useAppSelector(
    cartSlice.selectors.isPayingWithStripe
  )

  const isLoading = isPayingWithCard || isCreatingCart || isPayingWithStripe

  const { t } = useTranslation()

  const [showDialogStatus, setShowDialogStatus] = useState<
    'closed' | 'booking-policy' | 'refund-policy'
  >('closed')

  const ctaLabel = useMemo(() => {
    const formattedPrice = formatPrice(props.total)

    if (isKiosk) {
      return t('cta.goToPayment')
    }

    if (props.isFinalPayment) {
      if (props.paymentMethod === 'bank-transfer') {
        return t('cta.payWithBankTransfer')
      }

      if (props.paymentMethod === 'beach') {
        return t('cta.beachPayment')
      }

      return t('cta.confirmAndPay', { price: formattedPrice })
    }

    return t('cta.goToPaymentWithPrice', { price: formattedPrice })
  }, [props.total, props.isFinalPayment, props.paymentMethod])

  const refundPolicyDialogProps = useRefundPolicyDialogProps()

  return (
    <footer
      className={cn('flex flex-col gap-1', props.className, {
        'mt-auto py-4': isKiosk,
      })}
    >
      {!isKiosk && <RefundPolicy />}

      <div
        className={cn('flex flex-col gap-1', {
          'flex-col-reverse gap-4': isKiosk,
        })}
      >
        <div className={cn('flex', { 'gap-4': isKiosk })}>
          {isKiosk && <BackButton />}

          {props.nextPath ? (
            <ButtonLink
              className="flex-1"
              icon="ArrowRight"
              label={t('cta.continue')}
              to={props.nextPath}
            />
          ) : (
            <Button
              className="flex-1"
              disabled={props.isDisabled}
              isLoading={isLoading}
              type="submit"
            >
              {ctaLabel}
            </Button>
          )}
        </div>

        <p className="text-xs text-secondary">
          <TranslatedPolicyText
            beachPrivacyPolicy={beachPrivacyPolicy}
            onBookingPolicyDialogOpen={() =>
              setShowDialogStatus('booking-policy')
            }
            onRefundPolicyDialogOpen={() =>
              setShowDialogStatus('refund-policy')
            }
            regulation={regulation}
          />
        </p>
      </div>

      <BookingPolicyDialog
        bookingPolicy={regulation}
        isOpen={showDialogStatus === 'booking-policy'}
        setIsOpen={(isOpen) =>
          setShowDialogStatus(isOpen ? 'booking-policy' : 'closed')
        }
      />

      <RefundPolicyDialog
        {...refundPolicyDialogProps}
        isOpen={showDialogStatus === 'refund-policy'}
        setIsOpen={(isOpen) =>
          setShowDialogStatus(isOpen ? 'refund-policy' : 'closed')
        }
      />
    </footer>
  )
}
